import React, { useMemo, useState } from 'react';
import StandardPage from '../../components/StandardPage';
import { useApiKeys } from '../../context/api-keys';
import { useOrganization } from '../../context/organization';
import { trackApiKeyCopy } from '../../analytics';

const Home: React.FC = () => {
  const { apiKeys, loading } = useApiKeys();
  const { organization } = useOrganization();
  const [copied, setCopied] = useState<string | null>(null);

  const formatNumber = (value: number | string | null | undefined) => {
    const n = typeof value === 'number' ? value : Number(value ?? 0);
    return n.toLocaleString();
  };

  const creditsAvailable = organization?.credits ?? 0;
  const totalDatapoints = useMemo(
    () => apiKeys.reduce((sum, key) => sum + Number(key.used_quota ?? 0), 0),
    [apiKeys]
  );
  const totalKeys = apiKeys.length;
  const eurPerCredits = 100000;
  const eurEquivalent = creditsAvailable / eurPerCredits;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    trackApiKeyCopy({ source: 'home_table' });
    setCopied(text);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <StandardPage title="Usage & Credits">
      <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:grid-cols-3">
      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Available credits</p>
        <p className="mt-2 text-3xl font-bold text-blue-900">{creditsAvailable.toLocaleString()}</p>
        <p className="text-xs text-blue-700">≈ €{eurEquivalent.toLocaleString(undefined, { maximumFractionDigits: 6 })}</p>
      </div>

        <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Datapoints queried</p>
          <p className="mt-2 text-3xl font-bold text-emerald-900">{totalDatapoints.toLocaleString()}</p>
          <p className="text-xs text-emerald-700">Total datapoints fetched across all API keys.</p>
        </div>
        <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Active API keys</p>
          <p className="mt-2 text-3xl font-bold text-indigo-900">{totalKeys}</p>
          <p className="text-xs text-indigo-700">Keys available for your workspace.</p>
        </div>
      </div>
      <p className="mt-4 mb-4 ml-1 text-sm text-slate-600">
        Usage is now billed per datapoint. Conflict events, displacement, funding, and infrastructure data each deduct credits according to their source.
        ACLED points cost 1 credit with a license or 1,000 credits without one.
      </p>
      <div className="overflow-x-auto">
        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className="animate-pulse flex items-center justify-between rounded-lg border border-gray-200 p-4 shadow-sm">
                <div className="h-4 w-1/3 rounded bg-gray-300"></div>
                <div className="h-4 w-1/5 rounded bg-gray-200"></div>
                <div className="h-4 w-1/5 rounded bg-gray-200"></div>
                <div className="h-4 w-1/5 rounded bg-gray-200"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500">
                <tr>
                  {['Key', 'Created', 'Datapoints Queried', 'Credits Remaining'].map((col) => (
                    <th
                      key={col}
                      scope="col"
                      className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider text-white"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {apiKeys.map((item, index) => (
                  <tr
                    key={index}
                    className="group transition hover:bg-slate-50"
                  >
                    <td className="px-6 py-4 text-sm font-mono text-gray-800 flex items-center">
                      <span className="truncate max-w-[200px]">{item.api_key}</span>
                      <button
                        onClick={() => copyToClipboard(item.api_key)}
                        className="ml-2 rounded p-1 text-slate-500 transition hover:bg-slate-100 hover:text-indigo-600"
                        title="Copy to clipboard"
                      >
                        {copied === item.api_key ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-7.071 7.071a1 1 0 01-1.414 0L3.293 9.879a1 1 0 111.414-1.414l4.219 4.218 6.364-6.364a1 1 0 011.417 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M16 1H4a2 2 0 0 0-2 2v14h2V3h12V1zm-1 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H8V7h7v14z"/>
                          </svg>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(item.date_created).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-indigo-600 font-medium">
                      {formatNumber(item.used_quota)}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-emerald-600">
                      {formatNumber(creditsAvailable)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </StandardPage>
  );
};

export default Home;
